import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";

import * as React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Footer from "../footer/Footer";
import ParentLoginDialog from "../../pages/ParentLogin";
import { verifyTokenApi } from "../../api/parentApi";
import { useNavigate } from "react-router-dom";


const drawerWidth = 260;

const navItems = [
  { title: "Home", path: "/" },
  { title: "About", path: "/about" },
  { title: "Contact Us", path: "/contact" },
  { title: "Pricing", path: "/pricing" },
];

function DrawerAppBar(props) {
  const { window: windowProp } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [loginAnchor, setLoginAnchor] = React.useState(null);
  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const [parentInfo, setParentInfo] = React.useState(null);
  const [openLogin, setOpenLogin] = React.useState(false);

  const openLoginMenu = Boolean(loginAnchor);
  const openProfileMenu = Boolean(profileAnchor);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLoginMenuOpen = (event) => {
    setLoginAnchor(event.currentTarget);
  };

  const handleLoginMenuClose = () => {
    setLoginAnchor(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("parentInfo");
    localStorage.removeItem("parentToken");
    setParentInfo(null);
    handleProfileMenuClose();
  };

  React.useEffect(() => {
    const updateUser = () => {
      const storedParent = localStorage.getItem("parentInfo");
      if (storedParent) {
        setParentInfo(JSON.parse(storedParent));
      } else {
        setParentInfo(null);
      }
    };

    const handleOpenLogin = () => {
      setOpenLogin((prev) => prev || true);
    };

    // ✅ initial load
    updateUser();

    // ✅ listen for login updates
    window.addEventListener("userUpdated", updateUser);

    // ✅ listen for login dialog trigger
    window.addEventListener("openLoginDialog", handleOpenLogin);

    return () => {
      window.removeEventListener("userUpdated", updateUser);
      window.removeEventListener("openLoginDialog", handleOpenLogin);
    };
  }, []);

  // ✅ SAFE container handling
  const container =
    typeof windowProp === "function"
      ? () => windowProp().document.body
      : windowProp?.document?.body;

  const drawer = (
    <Box sx={{ textAlign: "center", mt: 3 }}>
      {/* Logo */}
      <Box
        component={Link}
        to="/"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          textDecoration: "none",
          mb: 2,
        }}
      >
        <img src="/logo.png" alt="logo" style={{ height: 40 }} />

        <Typography fontWeight={700} color="#152259">
          MySchoolling
        </Typography>
      </Box>

      <Divider />

      <List>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  bgcolor: isActive ? "#E8EEFF" : "transparent",
                  fontWeight: isActive ? 600 : 500,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#EEF3FF",
                    transform: "translateX(4px)",
                  },
                }}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          );
        })}

        <Divider sx={{ my: 1 }} />

        {/* MOBILE AUTH MENU */}
        {!parentInfo ? (
          <>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#EEF3FF",
                    transform: "translateX(4px)", // 🔥 subtle motion
                  },
                }}
                onClick={() => {
                  setMobileOpen(false);
                  setOpenLogin(true);
                }}
              >
                <ListItemText primary="Login as Parent" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#EEF3FF",
                    transform: "translateX(4px)", // 🔥 subtle motion
                  },
                }}
                onClick={() => {
                  window.location.href = "/";
                }}
              >
                <ListItemText primary="Login as School" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            {/* 👤 Parent Info (Clean UI) */}
            <Box sx={{ px: 2, py: 2, textAlign: "left" }}>
              <Typography fontWeight={600}>
                {parentInfo.name}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {parentInfo.email}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "inline-block",
                  mt: 1,
                  px: 1,
                  py: 0.3,
                  borderRadius: 1,
                  bgcolor: "#EEF4FF",
                  color: "#1976d2",
                  fontWeight: 600,
                }}
              >
                {parentInfo?.planName || "Free Plan"}
              </Typography>
            </Box>

            <Divider />

            {/* 🚪 Logout */}
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#EEF3FF",
                    transform: "translateX(4px)", // 🔥 subtle motion
                    transform: "scale(1.02)"
                  },
                }}
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
              >
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ color: "error.main", fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <CssBaseline />

      {/* APPBAR */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar
          sx={{
            height: 72,
            display: "flex",
            alignItems: "center",
            maxWidth: 1280,
            width: "100%",
            margin: "0 auto",
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Mobile Menu */}
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
              }}
            >
              <img src="/logo.png" alt="logo" style={{ height: 36 }} />
              <Typography fontWeight={700} color="#152259">
                MySchoolling
              </Typography>
            </Box>
          </Box>

          {/* RIGHT SIDE */}
          <Box
            sx={{
              ml: "auto", // 🔥 THIS FIXES ALIGNMENT
              display: { xs: "none", md: "flex" },
              gap: 4,
              alignItems: "center",
            }}
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Box
                  key={item.title}
                  component={Link}
                  to={item.path}
                  sx={{
                    textDecoration: "none",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#152259" : "#555",
                    position: "relative",

                    "&:hover": { color: "#152259" },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: isActive ? "100%" : "0%",
                      height: "2px",
                      bottom: -6,
                      left: 0,
                      backgroundColor: "#152259",
                      transition: "width 0.3s",
                    },

                    "&:hover::after": { width: "100%" },
                  }}
                >
                  {item.title}
                </Box>
              );
            })}

            {/* LOGIN */}
            {!parentInfo ? (
              <>
                <Button
                  variant="contained"
                  onClick={handleLoginMenuOpen}
                  sx={{
                    backgroundColor: "#152259",
                    color: "#fff",
                    borderRadius: "30px",
                    px: 4,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Login
                </Button>

                <Menu
                  anchorEl={loginAnchor}
                  open={openLoginMenu}
                  onClose={handleLoginMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleLoginMenuClose();
                      setOpenLogin(true);
                    }}
                  >
                    Continue as Parent
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleLoginMenuClose();
                      window.location.href = "/";
                    }}
                  >
                    Continue as School
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Avatar
                  onClick={handleProfileMenuOpen}
                  sx={{
                    width: 32,
                    height: 32,
                    border: "2px solid #152259",
                    bgcolor: "#152259",
                    cursor: "pointer",
                  }}
                >
                  {parentInfo.name?.charAt(0)}
                </Avatar>

                {/* 🔥 IMPROVED MENU */}
                <Menu
                  anchorEl={profileAnchor}
                  open={openProfileMenu}
                  onClose={handleProfileMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      borderRadius: 3,
                      minWidth: 260,
                      boxShadow: "0px 10px 30px rgba(0,0,0,0.12)",
                      overflow: "hidden",
                    },
                  }}
                >
                  {/* 👤 USER HEADER */}
                  <Box
                    sx={{
                      px: 2,
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#152259" }}>
                      {parentInfo.name?.charAt(0)}
                    </Avatar>

                    <Box>
                      <Typography fontWeight={600} fontSize="0.9rem">
                        {parentInfo.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {parentInfo.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  {/* 📦 PLAN */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" fontWeight={400} color="text.secondary">
                      Current Plan
                    </Typography>

                    <Box mt={0.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 1.5,
                          bgcolor: "#EEF4FF",
                          color: "#1976d2",
                          fontWeight: 600,
                        }}
                      >
                        {parentInfo?.planName || "Free Plan"}
                      </Typography>
                    </Box>

                    {/* 🚀 UPGRADE BUTTON */}
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      sx={{
                        mt: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#152259",
                        "&:hover": {
                          backgroundColor: "#0f1a45",
                        },
                      }}
                      onClick={() => {
                        handleProfileMenuClose();
                        // 👉 navigate or open pricing
                        window.location.href = "/pricing#plans";
                      }}
                    >
                      Upgrade Plan
                    </Button>
                  </Box>

                  <Divider />

                  {/* 🚪 LOGOUT */}
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        bgcolor: "#FFF1F0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#D32F2F",
                        fontWeight: 500,
                        fontSize: "0.9rem",
                      }}
                    >
                      Logout
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        transitionDuration={300}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            backgroundColor: "#F7F9FC",
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* MAIN CONTENT */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* FOOTER */}
      <Footer />

      {/* PARENT LOGIN DIALOG */}
      <ParentLoginDialog
        open={openLogin}
        handleClose={() => {
          setOpenLogin(false);
          // Refresh user info after successful login
          const storedParent = localStorage.getItem("parentInfo");
          if (storedParent) {
            setParentInfo(JSON.parse(storedParent));
          }
        }}
      />
    </Box >
  );
}

export default DrawerAppBar;